import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Button, Stack, Chip, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, Dialog, DialogTitle,
  DialogContent, DialogActions, Tabs, Tab, MenuItem, IconButton, Alert,
  Switch, FormControlLabel, RadioGroup, Radio, FormLabel, FormControl,
  CircularProgress, Tooltip, InputAdornment
} from "@mui/material";
import {
  Add, Delete, Edit, LocalOffer, Discount, Schedule, Category as CategoryIcon,
  Campaign, Group as GroupIcon, Save
} from "@mui/icons-material";
import API from "../services/api";
import toast from "react-hot-toast";

const trMoney = (x) => Number(x || 0).toLocaleString("tr-TR") + " ₺";
const trDate = (x) => x ? new Date(x).toLocaleDateString("tr-TR") : "—";
const trDateTime = (x) => x ? new Date(x).toLocaleString("tr-TR") : "—";

export default function B2BPricing() {
  const [tab, setTab] = useState(0);

  // Shared
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);

  // Tab 1 — Groups
  const [groupDialog, setGroupDialog] = useState({ open: false, data: null });
  const [groupForm, setGroupForm] = useState({
    name: "", description: "", default_discount_pct: 0,
    min_order_amount: 0, payment_terms: "", shows_vat_inclusive: false
  });

  // Tab 2 — Category Discount Matrix
  const [matrix, setMatrix] = useState({}); // { "catId_groupId": pct }
  const [matrixSaving, setMatrixSaving] = useState(null); // key being saved

  // Tab 3 — Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDialog, setCampaignDialog] = useState({ open: false, data: null });
  const [campaignForm, setCampaignForm] = useState({
    name: "", description: "", discount_pct: 0, target_type: "all",
    target_group_id: "", target_category_id: "", target_product_id: "",
    starts_at: "", ends_at: "", min_qty: 1, is_active: true
  });

  // Tab 4 — Payment Terms (Vade)
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [vadeDialog, setVadeDialog] = useState({ open: false, data: null });
  const [vadeForm, setVadeForm] = useState({ name: "", days: 0, vade_farki_pct: 0, is_active: true });

  // Tab 5 — Tiers
  const [tiers, setTiers] = useState([]);
  const [tierDialog, setTierDialog] = useState({ open: false });
  const [tierForm, setTierForm] = useState({
    product_id: "", group_id: "", min_qty: 1, price: "",
    valid_from: "", valid_until: ""
  });

  // Tab 6 — Bulk
  const [bulkForm, setBulkForm] = useState({ category_id: "", group_id: "", discount_pct: "" });

  // Tab 7 — Coupons
  const [coupons, setCoupons] = useState([]);
  const [couponDialog, setCouponDialog] = useState({ open: false, data: null });
  const [couponForm, setCouponForm] = useState({
    code: "", discount_type: "percent", discount_value: 0,
    min_order_amount: 0, max_uses: 0, valid_from: "", valid_until: "",
    target_group_id: "", is_active: true
  });

  // ============ LOADERS ============
  const loadGroups = useCallback(async () => {
    try { setGroups((await API.get("/api/admin/b2b/groups")).data || []); } catch (e) {}
  }, []);
  const loadCategories = useCallback(async () => {
    try { setCategories((await API.get("/api/categories")).data || []); } catch (e) {}
  }, []);
  const loadMatrix = useCallback(async () => {
    try {
      const data = (await API.get("/api/admin/b2b/category-discounts")).data || [];
      const map = {};
      data.forEach(r => { map[`${r.category_id}_${r.group_id}`] = r.discount_pct; });
      setMatrix(map);
    } catch (e) {}
  }, []);
  const loadCampaigns = useCallback(async () => {
    try { setCampaigns((await API.get("/api/admin/b2b/campaigns")).data || []); } catch (e) {}
  }, []);
  const loadPaymentTerms = useCallback(async () => {
    try { setPaymentTerms((await API.get("/api/admin/b2b/payment-terms")).data || []); } catch (e) {}
  }, []);
  const loadTiers = useCallback(async () => {
    try { setTiers((await API.get("/api/admin/b2b/tiers")).data || []); } catch (e) {}
  }, []);
  const loadCoupons = useCallback(async () => {
    try { setCoupons((await API.get("/api/admin/b2b/coupons")).data || []); } catch (e) {}
  }, []);

  useEffect(() => {
    loadGroups();
    loadCategories();
    loadMatrix();
    loadCampaigns();
    loadPaymentTerms();
    loadTiers();
    loadCoupons();
  }, [loadGroups, loadCategories, loadMatrix, loadCampaigns, loadPaymentTerms, loadTiers, loadCoupons]);

  // ============ TAB 1 — GROUPS ============
  const openGroupDialog = (g) => {
    if (g) setGroupForm({
      name: g.name || "", description: g.description || "",
      default_discount_pct: g.default_discount_pct || 0,
      min_order_amount: g.min_order_amount || 0,
      payment_terms: g.payment_terms || "",
      shows_vat_inclusive: !!g.shows_vat_inclusive
    });
    else setGroupForm({
      name: "", description: "", default_discount_pct: 0,
      min_order_amount: 0, payment_terms: "", shows_vat_inclusive: false
    });
    setGroupDialog({ open: true, data: g });
  };

  const saveGroup = async () => {
    if (!groupForm.name) return toast.error("Grup adı gerekli");
    try {
      if (groupDialog.data) {
        await API.put(`/api/admin/b2b/groups/${groupDialog.data.id}`, groupForm);
        toast.success("Grup güncellendi");
      } else {
        await API.post("/api/admin/b2b/groups", groupForm);
        toast.success("Grup eklendi");
      }
      setGroupDialog({ open: false, data: null });
      loadGroups();
    } catch (e) { toast.error(e.response?.data?.error || "Hata oluştu"); }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm("Bu grup silinsin mi? (Müşteriler Standart gruba taşınır)")) return;
    try {
      await API.delete(`/api/admin/b2b/groups/${id}`);
      toast.success("Silindi");
      loadGroups();
    } catch (e) { toast.error("Silinemedi"); }
  };

  // ============ TAB 2 — MATRIX ============
  const handleMatrixChange = (catId, groupId, value) => {
    setMatrix(m => ({ ...m, [`${catId}_${groupId}`]: value }));
  };

  const saveMatrixCell = async (catId, groupId, value) => {
    const key = `${catId}_${groupId}`;
    const pct = Number(value) || 0;
    if (pct < 0 || pct > 100) return toast.error("0-100 arası bir yüzde girin");
    setMatrixSaving(key);
    try {
      await API.post("/api/admin/b2b/category-discounts", {
        category_id: catId, group_id: groupId, discount_pct: pct
      });
      toast.success(`Kaydedildi: %${pct}`);
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally {
      setMatrixSaving(null);
    }
  };

  // ============ TAB 3 — CAMPAIGNS ============
  const openCampaignDialog = (c) => {
    if (c) setCampaignForm({
      name: c.name || "", description: c.description || "",
      discount_pct: c.discount_pct || 0,
      target_type: c.target_type || "all",
      target_group_id: c.target_group_id || "",
      target_category_id: c.target_category_id || "",
      target_product_id: c.target_product_id || "",
      starts_at: c.starts_at ? c.starts_at.slice(0, 16) : "",
      ends_at: c.ends_at ? c.ends_at.slice(0, 16) : "",
      min_qty: c.min_qty || 1,
      is_active: !!c.is_active
    });
    else setCampaignForm({
      name: "", description: "", discount_pct: 0, target_type: "all",
      target_group_id: "", target_category_id: "", target_product_id: "",
      starts_at: "", ends_at: "", min_qty: 1, is_active: true
    });
    setCampaignDialog({ open: true, data: c });
  };

  const saveCampaign = async () => {
    if (!campaignForm.name) return toast.error("Kampanya adı gerekli");
    if (!campaignForm.discount_pct) return toast.error("İndirim oranı gerekli");
    const payload = { ...campaignForm };
    // Clean unused target fields
    if (payload.target_type !== "group") payload.target_group_id = null;
    if (payload.target_type !== "category") payload.target_category_id = null;
    if (payload.target_type !== "product") payload.target_product_id = null;
    try {
      if (campaignDialog.data) {
        await API.put(`/api/admin/b2b/campaigns/${campaignDialog.data.id}`, payload);
        toast.success("Kampanya güncellendi");
      } else {
        await API.post("/api/admin/b2b/campaigns", payload);
        toast.success("Kampanya eklendi");
      }
      setCampaignDialog({ open: false, data: null });
      loadCampaigns();
    } catch (e) { toast.error(e.response?.data?.error || "Hata oluştu"); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Kampanya silinsin mi?")) return;
    try {
      await API.delete(`/api/admin/b2b/campaigns/${id}`);
      toast.success("Silindi");
      loadCampaigns();
    } catch (e) { toast.error("Silinemedi"); }
  };

  const campaignStatusChip = (c) => {
    const status = c.status || (c.is_active ? "aktif" : "pasif");
    const map = {
      aktif: { color: "success", label: "Aktif" },
      bekliyor: { color: "warning", label: "Bekliyor" },
      bitti: { color: "default", label: "Bitti" },
      pasif: { color: "default", label: "Pasif" }
    };
    const m = map[status] || { color: "default", label: status };
    return <Chip size="small" color={m.color} label={m.label} />;
  };

  const targetChip = (c) => {
    const labels = {
      all: "Tüm Ürünler", group: `Grup: ${groups.find(g => g.id === c.target_group_id)?.name || "?"}`,
      category: `Kat: ${categories.find(k => k.id === c.target_category_id)?.name || "?"}`,
      product: `Ürün #${c.target_product_id}`
    };
    return <Chip size="small" variant="outlined" label={labels[c.target_type] || c.target_type} />;
  };

  // ============ TAB 4 — VADE ============
  const openVadeDialog = (v) => {
    if (v) setVadeForm({
      name: v.name || "", days: v.days || 0,
      vade_farki_pct: v.vade_farki_pct || 0,
      is_active: !!v.is_active
    });
    else setVadeForm({ name: "", days: 0, vade_farki_pct: 0, is_active: true });
    setVadeDialog({ open: true, data: v });
  };

  const saveVade = async () => {
    if (!vadeForm.name) return toast.error("Vade adı gerekli");
    try {
      if (vadeDialog.data) {
        await API.put(`/api/admin/b2b/payment-terms/${vadeDialog.data.id}`, vadeForm);
        toast.success("Vade güncellendi");
      } else {
        await API.post("/api/admin/b2b/payment-terms", vadeForm);
        toast.success("Vade eklendi");
      }
      setVadeDialog({ open: false, data: null });
      loadPaymentTerms();
    } catch (e) { toast.error(e.response?.data?.error || "Hata oluştu"); }
  };

  const deleteVade = async (id) => {
    if (!window.confirm("Vade seçeneği silinsin mi?")) return;
    try {
      await API.delete(`/api/admin/b2b/payment-terms/${id}`);
      toast.success("Silindi");
      loadPaymentTerms();
    } catch (e) { toast.error("Silinemedi"); }
  };

  // ============ TAB 5 — TIERS ============
  const saveTier = async () => {
    if (!tierForm.product_id || !tierForm.price) return toast.error("Ürün ID ve fiyat gerekli");
    try {
      await API.post("/api/admin/b2b/tiers", tierForm);
      toast.success("Özel fiyat eklendi");
      setTierDialog({ open: false });
      setTierForm({ product_id: "", group_id: "", min_qty: 1, price: "", valid_from: "", valid_until: "" });
      loadTiers();
    } catch (e) { toast.error(e.response?.data?.error || "Hata"); }
  };

  const deleteTier = async (id) => {
    if (!window.confirm("Özel fiyat silinsin mi?")) return;
    try { await API.delete(`/api/admin/b2b/tiers/${id}`); toast.success("Silindi"); loadTiers(); }
    catch (e) { toast.error("Silinemedi"); }
  };

  // ============ TAB 6 — BULK ============
  const applyBulk = async () => {
    if (!bulkForm.group_id || bulkForm.discount_pct === "") return toast.error("Grup ve indirim gerekli");
    if (!window.confirm(`${bulkForm.category_id ? "Seçili kategorideki" : "TÜM"} ürünlere %${bulkForm.discount_pct} indirim uygulanacak. Emin misiniz?`)) return;
    try {
      const r = await API.post("/api/admin/b2b/tiers/bulk", bulkForm);
      toast.success(`${r.data.inserted || 0} ürün için fiyat eklendi`);
      loadTiers();
    } catch (e) { toast.error("Hata oluştu"); }
  };

  // ============ TAB 7 — COUPONS ============
  const openCouponDialog = (c) => {
    if (c) setCouponForm({
      code: c.code || "", discount_type: c.discount_type || "percent",
      discount_value: c.discount_value || 0,
      min_order_amount: c.min_order_amount || 0,
      max_uses: c.max_uses || 0,
      valid_from: c.valid_from ? c.valid_from.slice(0, 10) : "",
      valid_until: c.valid_until ? c.valid_until.slice(0, 10) : "",
      target_group_id: c.target_group_id || "",
      is_active: !!c.is_active
    });
    else setCouponForm({
      code: "", discount_type: "percent", discount_value: 0,
      min_order_amount: 0, max_uses: 0, valid_from: "", valid_until: "",
      target_group_id: "", is_active: true
    });
    setCouponDialog({ open: true, data: c });
  };

  const saveCoupon = async () => {
    if (!couponForm.code) return toast.error("Kupon kodu gerekli");
    if (!couponForm.discount_value) return toast.error("İndirim değeri gerekli");
    const payload = { ...couponForm, code: couponForm.code.toUpperCase() };
    try {
      if (couponDialog.data) {
        await API.put(`/api/admin/b2b/coupons/${couponDialog.data.id}`, payload);
        toast.success("Kupon güncellendi");
      } else {
        await API.post("/api/admin/b2b/coupons", payload);
        toast.success("Kupon eklendi");
      }
      setCouponDialog({ open: false, data: null });
      loadCoupons();
    } catch (e) { toast.error(e.response?.data?.error || "Hata oluştu"); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Kupon silinsin mi?")) return;
    try {
      await API.delete(`/api/admin/b2b/coupons/${id}`);
      toast.success("Silindi");
      loadCoupons();
    } catch (e) { toast.error("Silinemedi"); }
  };

  // ============ RENDER ============
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>💰 B2B Fiyatlandırma & Kampanya</Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab icon={<GroupIcon fontSize="small" />} iconPosition="start" label="👥 Gruplar" />
        <Tab icon={<CategoryIcon fontSize="small" />} iconPosition="start" label="📊 Matris" />
        <Tab icon={<Campaign fontSize="small" />} iconPosition="start" label="🎯 Kampanyalar" />
        <Tab icon={<Schedule fontSize="small" />} iconPosition="start" label="💳 Vade" />
        <Tab icon={<Discount fontSize="small" />} iconPosition="start" label="💰 Özel Fiyat" />
        <Tab label="📦 Toplu" />
        <Tab icon={<LocalOffer fontSize="small" />} iconPosition="start" label="🎟️ Kuponlar" />
      </Tabs>

      {/* ============ TAB 1 — GROUPS ============ */}
      {tab === 0 && (
        <Card>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              B2B müşteri grupları için <b>KDV Hariç</b> bırakın. Perakende (B2C) gruplar için <b>KDV Dahil</b> açın. Grup, müşterinin gördüğü tüm fiyatları belirler.
            </Alert>
            <Stack direction="row" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>Müşteri Grupları</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => openGroupDialog(null)}>Yeni Grup</Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Grup</b></TableCell>
                  <TableCell><b>Açıklama</b></TableCell>
                  <TableCell align="right"><b>Default İnd.</b></TableCell>
                  <TableCell align="right"><b>Min Sipariş</b></TableCell>
                  <TableCell><b>Vade</b></TableCell>
                  <TableCell><b>KDV Gösterimi</b></TableCell>
                  <TableCell align="right"><b>Müşteri</b></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map(g => (
                  <TableRow key={g.id} hover>
                    <TableCell><b>{g.name}</b></TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{g.description}</TableCell>
                    <TableCell align="right">%{g.default_discount_pct}</TableCell>
                    <TableCell align="right">{trMoney(g.min_order_amount)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{g.payment_terms || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={g.shows_vat_inclusive ? "Dahil" : "Hariç"}
                        color={g.shows_vat_inclusive ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">{g.user_count || 0}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openGroupDialog(g)}><Edit fontSize="small" /></IconButton>
                      {g.id !== 1 && (
                        <IconButton size="small" color="error" onClick={() => deleteGroup(g.id)}><Delete fontSize="small" /></IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {groups.length === 0 && (
                  <TableRow><TableCell colSpan={8}><Alert severity="info">Henüz grup yok.</Alert></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ============ TAB 2 — MATRIX ============ */}
      {tab === 1 && (
        <Card>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Hücreye yüzde girin (örn: <b>15</b>). Boş veya <b>0</b> = indirim yok. Hücreden çıkınca <b>otomatik kaydedilir</b>.
            </Alert>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>Kategori × Müşteri Grubu İndirim Matrisi</Typography>
            {categories.length === 0 || groups.length === 0 ? (
              <Alert severity="warning">Önce kategori ve grup tanımlayın.</Alert>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 180, bgcolor: "#fafafa" }}><b>Kategori \ Grup</b></TableCell>
                      {groups.map(g => (
                        <TableCell key={g.id} align="center" sx={{ bgcolor: "#fafafa" }}><b>{g.name}</b></TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categories.map(cat => (
                      <TableRow key={cat.id} hover>
                        <TableCell><b>{cat.name}</b></TableCell>
                        {groups.map(g => {
                          const key = `${cat.id}_${g.id}`;
                          return (
                            <TableCell key={g.id} align="center">
                              <TextField
                                size="small"
                                type="number"
                                value={matrix[key] ?? ""}
                                onChange={(e) => handleMatrixChange(cat.id, g.id, e.target.value)}
                                onBlur={(e) => saveMatrixCell(cat.id, g.id, e.target.value)}
                                inputProps={{ min: 0, max: 100, style: { textAlign: "center", padding: "4px" } }}
                                sx={{ width: 80 }}
                                InputProps={{
                                  endAdornment: matrixSaving === key
                                    ? <CircularProgress size={12} />
                                    : <InputAdornment position="end" sx={{ ml: 0 }}>%</InputAdornment>
                                }}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ TAB 3 — CAMPAIGNS ============ */}
      {tab === 2 && (
        <Card>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Aynı anda birden fazla kampanya çalışabilir. Müşteriye <b>en düşük fiyat</b> gösterilir.
            </Alert>
            <Stack direction="row" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>Kampanyalar</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => openCampaignDialog(null)}>Yeni Kampanya</Button>
            </Stack>
            {campaigns.length === 0 ? (
              <Alert severity="info">Henüz kampanya yok. <b>Yeni Kampanya</b> ile başlayın.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Ad</b></TableCell>
                    <TableCell align="right"><b>İndirim</b></TableCell>
                    <TableCell><b>Hedef</b></TableCell>
                    <TableCell><b>Başlangıç</b></TableCell>
                    <TableCell><b>Bitiş</b></TableCell>
                    <TableCell align="right"><b>Min Adet</b></TableCell>
                    <TableCell><b>Durum</b></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map(c => (
                    <TableRow key={c.id} hover>
                      <TableCell><b>{c.name}</b></TableCell>
                      <TableCell align="right">%{c.discount_pct}</TableCell>
                      <TableCell>{targetChip(c)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{trDateTime(c.starts_at)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{trDateTime(c.ends_at)}</TableCell>
                      <TableCell align="right">{c.min_qty || 1}</TableCell>
                      <TableCell>{campaignStatusChip(c)}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => openCampaignDialog(c)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteCampaign(c.id)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ TAB 4 — VADE ============ */}
      {tab === 3 && (
        <Card>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Müşteri sepette vade seçer. Vade farkı <b>KDV dahil tutar</b> üzerinden eklenir. Standart sektör değerleri yüklü; ihtiyaca göre değiştirin.
            </Alert>
            <Stack direction="row" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>Vade Seçenekleri</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => openVadeDialog(null)}>Yeni Vade</Button>
            </Stack>
            {paymentTerms.length === 0 ? (
              <Alert severity="info">Henüz vade seçeneği yok.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Vade Adı</b></TableCell>
                    <TableCell align="right"><b>Gün Sayısı</b></TableCell>
                    <TableCell align="right"><b>Vade Farkı</b></TableCell>
                    <TableCell><b>Aktif</b></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentTerms.map(v => (
                    <TableRow
                      key={v.id} hover sx={{ cursor: "pointer" }}
                      onClick={() => openVadeDialog(v)}
                    >
                      <TableCell><b>{v.name}</b></TableCell>
                      <TableCell align="right">{v.days} gün</TableCell>
                      <TableCell align="right">%{Number(v.vade_farki_pct).toLocaleString("tr-TR")}</TableCell>
                      <TableCell>
                        <Chip size="small" label={v.is_active ? "Aktif" : "Pasif"} color={v.is_active ? "success" : "default"} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" onClick={() => openVadeDialog(v)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteVade(v.id)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ TAB 5 — TIERS ============ */}
      {tab === 4 && (
        <Card>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Belirli bir ürüne, belirli bir grup veya müşteri için <b>özel fiyat</b> tanımlayın. Bu fiyat normal indirimleri devre dışı bırakır.
            </Alert>
            <Stack direction="row" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>Özel Fiyatlar (Tier)</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setTierDialog({ open: true })}>Yeni Tier</Button>
            </Stack>
            {tiers.length === 0 ? (
              <Alert severity="info">Henüz özel fiyat tanımlanmamış. <b>Toplu İndirim</b> sekmesinden hızlıca ekleyebilirsiniz.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Ürün</b></TableCell>
                    <TableCell><b>Grup/Müşteri</b></TableCell>
                    <TableCell align="right"><b>Min Adet</b></TableCell>
                    <TableCell align="right"><b>Fiyat</b></TableCell>
                    <TableCell><b>Geçerlilik</b></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tiers.map(t => (
                    <TableRow key={t.id} hover>
                      <TableCell>{t.product_name || `#${t.product_id}`}</TableCell>
                      <TableCell>{t.group_name || t.user_name || "—"}</TableCell>
                      <TableCell align="right">{t.min_qty}</TableCell>
                      <TableCell align="right">{Number(t.price).toFixed(2)} ₺</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>
                        {t.valid_from ? `${trDate(t.valid_from)} – ${t.valid_until ? trDate(t.valid_until) : "∞"}` : "Süresiz"}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => deleteTier(t.id)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ TAB 6 — BULK ============ */}
      {tab === 5 && (
        <Card>
          <CardContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              ⚠️ <b>Eski yöntem:</b> Sürekli kurallar için <b>Kategori İndirim Matrisi</b> sekmesini kullanın. Toplu İndirim, sabit fiyatlı ürünlere <b>tek seferlik kampanya</b> uygulamak içindir. Mevcut tier'lar etkilenmez (yenisi eklenir).
            </Alert>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>Toplu İndirim Uygula</Typography>
            <Stack spacing={2} maxWidth={500}>
              <TextField
                select label="Kategori (boş = tüm ürünler)"
                value={bulkForm.category_id}
                onChange={(e) => setBulkForm({ ...bulkForm, category_id: e.target.value })}
                helperText="Sadece bu kategorideki ürünler etkilenir."
              >
                <MenuItem value="">— Tüm Ürünler —</MenuItem>
                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
              <TextField
                select label="Müşteri Grubu *"
                value={bulkForm.group_id}
                onChange={(e) => setBulkForm({ ...bulkForm, group_id: e.target.value })}
                helperText="Hangi gruba indirim uygulanacak?"
              >
                {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </TextField>
              <TextField
                label="İndirim Oranı (%)"
                type="number"
                value={bulkForm.discount_pct}
                onChange={(e) => setBulkForm({ ...bulkForm, discount_pct: e.target.value })}
                helperText="Örn: 15 = %15 indirim"
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
              <Button variant="contained" color="warning" startIcon={<Save />} onClick={applyBulk}>Toplu Uygula</Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ============ TAB 7 — COUPONS ============ */}
      {tab === 6 && (
        <Card>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Müşteri sepette kupon kodunu girer. Yüzde veya sabit tutar indirimi uygulayabilirsiniz.
            </Alert>
            <Stack direction="row" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>İndirim Kuponları</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => openCouponDialog(null)}>Yeni Kupon</Button>
            </Stack>
            {coupons.length === 0 ? (
              <Alert severity="info">
                Henüz kupon yok. <b>Yeni Kupon</b> oluşturarak müşterilere indirim kodu verebilirsiniz (örn: <b>WELCOME10</b>).
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Kod</b></TableCell>
                    <TableCell><b>Tür</b></TableCell>
                    <TableCell align="right"><b>Değer</b></TableCell>
                    <TableCell align="right"><b>Min Sepet</b></TableCell>
                    <TableCell align="center"><b>Kullanım</b></TableCell>
                    <TableCell><b>Geçerlilik</b></TableCell>
                    <TableCell><b>Hedef Grup</b></TableCell>
                    <TableCell><b>Aktif</b></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coupons.map(c => (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        <Chip
                          label={c.code} color="primary" size="medium"
                          sx={{ fontWeight: 700, letterSpacing: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined"
                          label={c.discount_type === "percent" ? "%" : "₺"} />
                      </TableCell>
                      <TableCell align="right">
                        {c.discount_type === "percent" ? `%${c.discount_value}` : trMoney(c.discount_value)}
                      </TableCell>
                      <TableCell align="right">{c.min_order_amount > 0 ? trMoney(c.min_order_amount) : "—"}</TableCell>
                      <TableCell align="center">
                        {c.used_count || 0} / {c.max_uses > 0 ? c.max_uses : "∞"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>
                        {trDate(c.valid_from)} – {c.valid_until ? trDate(c.valid_until) : "∞"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {c.target_group_id
                          ? (groups.find(g => g.id === c.target_group_id)?.name || "—")
                          : "Tüm Gruplar"}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={c.is_active ? "Aktif" : "Pasif"} color={c.is_active ? "success" : "default"} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => openCouponDialog(c)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteCoupon(c.id)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ DIALOG: GROUP ============ */}
      <Dialog open={groupDialog.open} onClose={() => setGroupDialog({ open: false, data: null })} fullWidth maxWidth="sm">
        <DialogTitle>{groupDialog.data ? "Grubu Düzenle" : "Yeni Müşteri Grubu"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Grup Adı *" value={groupForm.name}
              onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
              helperText="Örn: Bayi, Toptancı, Perakende"
            />
            <TextField
              label="Açıklama" value={groupForm.description || ""}
              onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
              helperText="İç kullanım notu (müşteri görmez)"
            />
            <TextField
              label="Default İndirim Oranı"
              type="number" value={groupForm.default_discount_pct}
              onChange={(e) => setGroupForm({ ...groupForm, default_discount_pct: e.target.value })}
              helperText="Tüm ürünlere uygulanacak temel indirim. Örn: 10 = %10"
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
            <TextField
              label="Min. Sipariş Tutarı"
              type="number" value={groupForm.min_order_amount}
              onChange={(e) => setGroupForm({ ...groupForm, min_order_amount: e.target.value })}
              helperText="Bu tutarın altında sipariş veremez. Örn: 1000"
              InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
            />
            <TextField
              label="Vade / Ödeme Şartı (serbest metin)"
              value={groupForm.payment_terms || ""}
              onChange={(e) => setGroupForm({ ...groupForm, payment_terms: e.target.value })}
              helperText="Örn: 30 gün vade, peşin"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!groupForm.shows_vat_inclusive}
                  onChange={(e) => setGroupForm({ ...groupForm, shows_vat_inclusive: e.target.checked })}
                />
              }
              label="KDV Dahil Göster (B2C için)"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Açık = fiyatlar KDV dahil; Kapalı = KDV hariç (B2B standart)
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialog({ open: false, data: null })}>İptal</Button>
          <Button variant="contained" onClick={saveGroup}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      {/* ============ DIALOG: CAMPAIGN ============ */}
      <Dialog open={campaignDialog.open} onClose={() => setCampaignDialog({ open: false, data: null })} fullWidth maxWidth="md">
        <DialogTitle>{campaignDialog.data ? "Kampanyayı Düzenle" : "Yeni Kampanya"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Kampanya Adı *" value={campaignForm.name}
              onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
              helperText="Örn: Yılbaşı İndirimi, Bahar Kampanyası"
            />
            <TextField
              label="Açıklama" multiline rows={2}
              value={campaignForm.description}
              onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
            />
            <TextField
              label="İndirim Oranı *"
              type="number" value={campaignForm.discount_pct}
              onChange={(e) => setCampaignForm({ ...campaignForm, discount_pct: e.target.value })}
              helperText="Örn: 20 = %20 indirim"
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
            <TextField
              select label="Hedef Türü *"
              value={campaignForm.target_type}
              onChange={(e) => setCampaignForm({ ...campaignForm, target_type: e.target.value })}
              helperText="Kampanya hangi ürün/grup/kategori için geçerli?"
            >
              <MenuItem value="all">Tüm Ürünler</MenuItem>
              <MenuItem value="group">Belirli Müşteri Grubu</MenuItem>
              <MenuItem value="category">Belirli Kategori</MenuItem>
              <MenuItem value="product">Belirli Ürün</MenuItem>
            </TextField>

            {campaignForm.target_type === "group" && (
              <TextField
                select label="Hedef Grup *"
                value={campaignForm.target_group_id}
                onChange={(e) => setCampaignForm({ ...campaignForm, target_group_id: e.target.value })}
              >
                {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </TextField>
            )}

            {campaignForm.target_type === "category" && (
              <TextField
                select label="Hedef Kategori *"
                value={campaignForm.target_category_id}
                onChange={(e) => setCampaignForm({ ...campaignForm, target_category_id: e.target.value })}
              >
                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            )}

            {campaignForm.target_type === "product" && (
              <TextField
                label="Ürün ID *"
                type="number" value={campaignForm.target_product_id}
                onChange={(e) => setCampaignForm({ ...campaignForm, target_product_id: e.target.value })}
                helperText="Ürünler sayfasından ID bakabilirsiniz."
              />
            )}

            <Stack direction="row" spacing={2}>
              <TextField
                label="Başlangıç Tarihi" type="datetime-local" fullWidth
                InputLabelProps={{ shrink: true }}
                value={campaignForm.starts_at}
                onChange={(e) => setCampaignForm({ ...campaignForm, starts_at: e.target.value })}
              />
              <TextField
                label="Bitiş Tarihi" type="datetime-local" fullWidth
                InputLabelProps={{ shrink: true }}
                value={campaignForm.ends_at}
                onChange={(e) => setCampaignForm({ ...campaignForm, ends_at: e.target.value })}
              />
            </Stack>

            <TextField
              label="Minimum Adet"
              type="number" value={campaignForm.min_qty}
              onChange={(e) => setCampaignForm({ ...campaignForm, min_qty: e.target.value })}
              helperText="Bu adetten az alımda kampanya geçerli değil. Default: 1"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={!!campaignForm.is_active}
                  onChange={(e) => setCampaignForm({ ...campaignForm, is_active: e.target.checked })}
                />
              }
              label="Aktif"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDialog({ open: false, data: null })}>İptal</Button>
          <Button variant="contained" onClick={saveCampaign}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      {/* ============ DIALOG: VADE ============ */}
      <Dialog open={vadeDialog.open} onClose={() => setVadeDialog({ open: false, data: null })} fullWidth maxWidth="sm">
        <DialogTitle>{vadeDialog.data ? "Vade Düzenle" : "Yeni Vade"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Vade Adı *" value={vadeForm.name}
              onChange={(e) => setVadeForm({ ...vadeForm, name: e.target.value })}
              helperText="Örn: Peşin, 30 Gün, 60 Gün, 90 Gün"
            />
            <TextField
              label="Gün Sayısı"
              type="number" value={vadeForm.days}
              onChange={(e) => setVadeForm({ ...vadeForm, days: e.target.value })}
              helperText="Peşin için 0 girin. Örn: 30"
            />
            <TextField
              label="Vade Farkı"
              type="number" value={vadeForm.vade_farki_pct}
              onChange={(e) => setVadeForm({ ...vadeForm, vade_farki_pct: e.target.value })}
              helperText="Sepet tutarına eklenecek yüzde. Örn: 2.5 = %2.5"
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!vadeForm.is_active}
                  onChange={(e) => setVadeForm({ ...vadeForm, is_active: e.target.checked })}
                />
              }
              label="Aktif (müşteri sepette görsün)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVadeDialog({ open: false, data: null })}>İptal</Button>
          <Button variant="contained" onClick={saveVade}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      {/* ============ DIALOG: TIER ============ */}
      <Dialog open={tierDialog.open} onClose={() => setTierDialog({ open: false })} fullWidth maxWidth="sm">
        <DialogTitle>Yeni Özel Fiyat (Tier)</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Ürün ID *" type="number" value={tierForm.product_id}
              onChange={(e) => setTierForm({ ...tierForm, product_id: e.target.value })}
              helperText="Ürünler sayfasından ID bakabilirsiniz."
            />
            <TextField
              select label="Müşteri Grubu" value={tierForm.group_id}
              onChange={(e) => setTierForm({ ...tierForm, group_id: e.target.value })}
              helperText="Boş bırakırsanız, tek müşteri için kullanıcı bazlı tanımlanır."
            >
              <MenuItem value="">— Kullanıcı bazlı (grup yok) —</MenuItem>
              {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </TextField>
            <TextField
              label="Min. Adet" type="number" value={tierForm.min_qty}
              onChange={(e) => setTierForm({ ...tierForm, min_qty: e.target.value })}
              helperText="Bu adetten itibaren özel fiyat geçerli."
            />
            <TextField
              label="Fiyat *" type="number" value={tierForm.price}
              onChange={(e) => setTierForm({ ...tierForm, price: e.target.value })}
              helperText="Birim fiyat (KDV hariç)."
              InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Başlangıç" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={tierForm.valid_from}
                onChange={(e) => setTierForm({ ...tierForm, valid_from: e.target.value })}
              />
              <TextField
                label="Bitiş" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={tierForm.valid_until}
                onChange={(e) => setTierForm({ ...tierForm, valid_until: e.target.value })}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTierDialog({ open: false })}>İptal</Button>
          <Button variant="contained" onClick={saveTier}>Ekle</Button>
        </DialogActions>
      </Dialog>

      {/* ============ DIALOG: COUPON ============ */}
      <Dialog open={couponDialog.open} onClose={() => setCouponDialog({ open: false, data: null })} fullWidth maxWidth="sm">
        <DialogTitle>{couponDialog.data ? "Kuponu Düzenle" : "Yeni Kupon"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Kupon Kodu *"
              value={couponForm.code}
              onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
              helperText="Müşteri sepette girer. Örn: WELCOME10"
              inputProps={{ style: { textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 } }}
            />
            <FormControl>
              <FormLabel>Tür *</FormLabel>
              <RadioGroup
                row value={couponForm.discount_type}
                onChange={(e) => setCouponForm({ ...couponForm, discount_type: e.target.value })}
              >
                <FormControlLabel value="percent" control={<Radio />} label="Yüzde İndirim (%)" />
                <FormControlLabel value="fixed" control={<Radio />} label="Sabit Tutar İndirim (₺)" />
              </RadioGroup>
            </FormControl>
            <TextField
              label="Değer *"
              type="number" value={couponForm.discount_value}
              onChange={(e) => setCouponForm({ ...couponForm, discount_value: e.target.value })}
              helperText={couponForm.discount_type === "percent" ? "Örn: 10 = %10 indirim" : "Örn: 50 = 50 ₺ indirim"}
              InputProps={{
                endAdornment: <InputAdornment position="end">{couponForm.discount_type === "percent" ? "%" : "₺"}</InputAdornment>
              }}
            />
            <TextField
              label="Min. Sepet Tutarı"
              type="number" value={couponForm.min_order_amount}
              onChange={(e) => setCouponForm({ ...couponForm, min_order_amount: e.target.value })}
              helperText="0 = sınırsız (her sepete uygulanır)"
              InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
            />
            <TextField
              label="Maksimum Kullanım"
              type="number" value={couponForm.max_uses}
              onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })}
              helperText="0 = sınırsız (kaç kere kullanılabilir)"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Geçerlilik Başlangıç" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={couponForm.valid_from}
                onChange={(e) => setCouponForm({ ...couponForm, valid_from: e.target.value })}
              />
              <TextField
                label="Geçerlilik Bitiş" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={couponForm.valid_until}
                onChange={(e) => setCouponForm({ ...couponForm, valid_until: e.target.value })}
              />
            </Stack>
            <TextField
              select label="Hedef Grup"
              value={couponForm.target_group_id}
              onChange={(e) => setCouponForm({ ...couponForm, target_group_id: e.target.value })}
              helperText="Belirli gruba özel mi yoksa tüm müşterilere mi açık?"
            >
              <MenuItem value="">Tüm Gruplar</MenuItem>
              {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={!!couponForm.is_active}
                  onChange={(e) => setCouponForm({ ...couponForm, is_active: e.target.checked })}
                />
              }
              label="Aktif"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCouponDialog({ open: false, data: null })}>İptal</Button>
          <Button variant="contained" onClick={saveCoupon}>Kaydet</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
